import { CartAddEvent } from '@theme/events';

class ShoonyasProductReel extends HTMLElement {
  connectedCallback() {
    this.data = this.parseData();
    if (!this.data.length) {
      this.hidden = true;
      return;
    }

    this.launcher = this.querySelector('[data-reel-open]');
    this.dismissButton = this.querySelector('[data-reel-dismiss]');
    this.modal = this.querySelector('[data-reel-modal]');
    this.stage = this.querySelector('[data-reel-stage]');
    this.closeButton = this.querySelector('[data-reel-close]');
    this.muteButton = this.querySelector('[data-reel-mute]');
    this.soundIcon = this.querySelector('[data-reel-sound-icon]');
    this.mutedIcon = this.querySelector('[data-reel-muted-icon]');
    this.progressBar = this.querySelector('[data-reel-progress]');
    this.previewVideo = this.querySelector('.shoonyas-product-reel__preview');
    this.positionStorageKey = 'shoonyas-product-reel-position';
    this.soundStorageKey = 'shoonyas-product-reel-muted';
    this.index = 0;
    this.muted = this.getSessionMutedPreference();
    this.scrollRoot = this.getScrollRoot();
    this.lastScrollY = this.getClampedScrollY(this.getScrollY());
    this.upwardScrollDistance = 0;
    this.scrollDirectionThreshold = 4;
    this.scrollRevealThreshold = 28;
    this.bottomBoundaryTolerance = 12;
    this.lastFocusedElement = null;
    this.touchStart = null;
    this.mouseStart = null;
    this.widgetPointerId = null;
    this.widgetDragStart = null;
    this.widgetPosition = this.getStoredWidgetPosition();
    this.widgetDragMoved = false;
    this.suppressLauncherClick = false;
    this.idlePulseTimer = null;
    this.idlePulsePlayed = false;
    this.mouseTracked = false;
    this.mouseMoved = false;
    this.wheelDeltaX = 0;
    this.wheelDeltaY = 0;
    this.wheelResetTimer = null;
    this.lastWheelNavigation = 0;
    this.gestureThreshold = 60;
    this.progressFrame = null;
    this.activeVideo = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.renderSlides();
    this.bindEvents();
    this.applyStoredWidgetPosition();
    this.scheduleIdlePulse();
    this.updateMuteLabel();
    this.lazyLoadPreview();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.idlePulseTimer);
    window.clearTimeout(this.wheelResetTimer);
  }

  parseData() {
    const script = this.querySelector('[data-reel-data]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent).filter((item) => item.videoSrc);
    } catch (error) {
      console.error('Product reel data could not be parsed', error);
      return [];
    }
  }

  bindEvents() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.launcher?.addEventListener('click', (event) => this.onLauncherClick(event), { signal });
    this.launcher?.addEventListener('pointerdown', (event) => this.onWidgetPointerDown(event), { signal });
    document.addEventListener('pointermove', (event) => this.onWidgetPointerMove(event), { signal });
    document.addEventListener('pointerup', (event) => this.onWidgetPointerUp(event), { signal });
    document.addEventListener('pointercancel', (event) => this.onWidgetPointerCancel(event), { signal });
    this.dismissButton?.addEventListener('click', () => this.dismiss(), { signal });
    this.closeButton?.addEventListener('click', () => this.close(), { signal });
    this.muteButton?.addEventListener('click', () => this.toggleMute(), { signal });
    this.previewVideo?.addEventListener('play', () => this.updatePreviewPlayingState(), { signal });
    this.previewVideo?.addEventListener('pause', () => this.updatePreviewPlayingState(), { signal });
    this.querySelector('[data-reel-prev]')?.addEventListener('click', () => this.go(-1), { signal });
    this.querySelector('[data-reel-next]')?.addEventListener('click', () => this.go(1), { signal });
    this.stage?.addEventListener('touchstart', (event) => this.onTouchStart(event), { signal, passive: true });
    this.stage?.addEventListener('touchend', (event) => this.onTouchEnd(event), { signal });
    this.modal?.addEventListener('mousedown', (event) => this.onMouseDown(event), { signal, capture: true });
    this.modal?.addEventListener('dragstart', (event) => this.onDragStart(event), { signal, capture: true });
    this.modal?.addEventListener('wheel', (event) => this.onWheel(event), { signal, passive: false });
    document.addEventListener('mousemove', (event) => this.onMouseMove(event), { signal });
    document.addEventListener('mouseup', (event) => this.onMouseUp(event), { signal });
    this.addEventListener('click', (event) => this.onActionClick(event), { signal });
    this.scrollRoot.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
    window.addEventListener('resize', () => this.applyStoredWidgetPosition(), { signal });
    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
  }

  lazyLoadPreview() {
    const load = () => {
      this.loadVideoSources(this.previewVideo);
      if (!this.reduceMotion) this.previewVideo?.play().catch(() => {});
      this.updatePreviewPlayingState();
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(load, { timeout: 1800 });
    } else {
      window.setTimeout(load, 900);
    }
  }

  loadVideoSources(video) {
    if (!video || video.dataset.loaded === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.loaded = 'true';
    video.load();
  }

  renderSlides() {
    if (!this.stage) return;

    this.stage.innerHTML = this.data
      .map(
        (item, index) => `
          <article class="shoonyas-product-reel__slide" data-reel-index="${index}" data-active="${index === 0}">
            <video
              class="shoonyas-product-reel__video"
              playsinline
              draggable="false"
              preload="${index === 0 ? 'auto' : 'metadata'}"
              ${item.poster ? `poster="${this.escapeAttribute(item.poster)}"` : ''}
            >
              <source data-src="${this.escapeAttribute(item.videoSrc)}" type="${this.escapeAttribute(item.videoType || 'video/mp4')}">
            </video>
            <div class="shoonyas-product-reel__overlay">
              ${item.caption ? `<p class="shoonyas-product-reel__caption">${this.escapeHtml(item.caption)}</p>` : ''}
              <h2 class="shoonyas-product-reel__title">${this.escapeHtml(item.title)}</h2>
              <div class="shoonyas-product-reel__price">
                <span>${this.escapeHtml(item.price)}</span>
                ${item.compareAtPrice ? `<span class="shoonyas-product-reel__compare">${this.escapeHtml(item.compareAtPrice)}</span>` : ''}
              </div>
              <div class="shoonyas-product-reel__actions">
                <a class="shoonyas-product-reel__button" href="${this.escapeAttribute(item.url)}">View product</a>
                ${
                  item.available && item.variantId
                    ? `<button class="shoonyas-product-reel__button shoonyas-product-reel__button--ghost" type="button" data-reel-add data-variant-id="${this.escapeAttribute(String(item.variantId))}">Add to cart</button>`
                    : '<button class="shoonyas-product-reel__button shoonyas-product-reel__button--ghost" type="button" disabled>Sold out</button>'
                }
              </div>
            </div>
          </article>
        `
      )
      .join('');
  }

  open() {
    this.lastFocusedElement = document.activeElement;
    this.dataset.open = 'true';
    this.stopIdlePulse();
    document.body.classList.add('shoonyas-product-reel-open');
    this.modal?.setAttribute('aria-hidden', 'false');
    this.modal?.focus();
    this.previewVideo?.pause();
    this.updatePreviewPlayingState();
    this.activateSlide(this.index);
  }

  close() {
    this.dataset.open = 'false';
    document.body.classList.remove('shoonyas-product-reel-open');
    this.modal?.setAttribute('aria-hidden', 'true');
    this.stopProgress();
    this.resetWheelGesture();
    this.pauseAll();
    if (!this.reduceMotion) this.previewVideo?.play().catch(() => {});
    this.scheduleIdlePulse();
    if (this.lastFocusedElement instanceof HTMLElement) this.lastFocusedElement.focus();
  }

  dismiss() {
    this.dataset.dismissed = 'true';
    this.previewVideo?.pause();
    this.stopIdlePulse();
    this.updatePreviewPlayingState();
  }

  activateSlide(index) {
    this.index = (index + this.data.length) % this.data.length;
    const slides = Array.from(this.querySelectorAll('.shoonyas-product-reel__slide'));
    this.stopProgress(true);

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.index;
      slide.dataset.active = String(isActive);
      const video = slide.querySelector('video');

      if (!video) return;

      if (isActive) {
        this.loadVideoSources(video);
        video.muted = this.muted;
        video.currentTime = 0;
        this.activeVideo = video;
        this.startProgress(video);
        video.play().catch(() => {});
      } else {
        video.pause();
        if (Math.abs(slideIndex - this.index) === 1) this.loadVideoSources(video);
      }
    });

    this.updateMuteLabel();
  }

  go(direction) {
    if (this.data.length <= 1) return;
    this.activateSlide(this.index + direction);
  }

  toggleMute() {
    this.muted = !this.muted;
    this.storeSessionMutedPreference();
    const activeVideo = this.activeVideo || this.querySelector('.shoonyas-product-reel__slide[data-active="true"] video');
    if (activeVideo) activeVideo.muted = this.muted;
    this.updateMuteLabel();
  }

  updateMuteLabel() {
    if (!this.muteButton || !this.soundIcon || !this.mutedIcon) return;

    this.muteButton.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
    this.soundIcon.hidden = this.muted;
    this.mutedIcon.hidden = !this.muted;
  }

  pauseAll() {
    this.querySelectorAll('.shoonyas-product-reel__video').forEach((video) => video.pause());
  }

  startProgress(video) {
    this.activeVideo = video;
    this.updateProgress();
  }

  stopProgress(reset = false) {
    if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
    this.progressFrame = null;
    this.activeVideo = null;

    if (reset) this.setProgress(0);
  }

  updateProgress() {
    if (!this.activeVideo || this.dataset.open !== 'true') return;

    const duration = Number.isFinite(this.activeVideo.duration) ? this.activeVideo.duration : 0;
    const progress = duration > 0 ? (this.activeVideo.currentTime / duration) * 100 : 0;
    this.setProgress(progress);
    this.progressFrame = requestAnimationFrame(() => this.updateProgress());
  }

  setProgress(progress) {
    if (!this.progressBar) return;
    this.progressBar.style.setProperty('--reel-progress', `${Math.max(0, Math.min(progress, 100))}%`);
  }

  onScroll() {
    if (this.dataset.open === 'true') return;

    const maxScrollY = this.getMaxScrollY();
    const currentY = this.getClampedScrollY(this.getScrollY(), maxScrollY);
    const deltaY = currentY - this.lastScrollY;
    const passedRevealThreshold = currentY > 400;
    const wasAtBottom = this.lastScrollY >= maxScrollY - this.bottomBoundaryTolerance;
    const isNearBottom = currentY >= maxScrollY - this.bottomBoundaryTolerance;

    if (!passedRevealThreshold) {
      this.upwardScrollDistance = 0;
      this.dataset.scrollingDown = 'false';
      this.lastScrollY = currentY;
      return;
    }

    if (deltaY > this.scrollDirectionThreshold) {
      this.upwardScrollDistance = 0;
      this.dataset.scrollingDown = 'true';
    } else if (deltaY < -this.scrollDirectionThreshold) {
      if (wasAtBottom && isNearBottom) {
        this.lastScrollY = currentY;
        return;
      }

      this.upwardScrollDistance += Math.abs(deltaY);
      if (this.upwardScrollDistance >= this.scrollRevealThreshold) {
        this.dataset.scrollingDown = 'false';
      }
    }

    this.lastScrollY = currentY;
  }

  onLauncherClick(event) {
    if (this.suppressLauncherClick) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressLauncherClick = false;
      return;
    }

    this.markWidgetInteraction();
    this.open();
  }

  onWidgetPointerDown(event) {
    if (this.dataset.open === 'true') return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target instanceof Element && !event.target.closest('[data-reel-open]')) return;

    const rect = this.getBoundingClientRect();
    this.widgetPointerId = event.pointerId;
    this.widgetDragStart = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      left: rect.left,
      top: rect.top,
    };
    this.widgetDragMoved = false;
    this.dataset.dragging = 'true';
    this.dataset.snapping = 'false';
    this.stopIdlePulse();
    try {
      this.launcher?.setPointerCapture?.(event.pointerId);
    } catch (error) {
      // Pointer capture is helpful but not required for document-level drag tracking.
    }
  }

  onWidgetPointerMove(event) {
    if (this.widgetPointerId !== event.pointerId || !this.widgetDragStart) return;

    const deltaX = event.clientX - this.widgetDragStart.pointerX;
    const deltaY = event.clientY - this.widgetDragStart.pointerY;
    if (!this.widgetDragMoved && Math.hypot(deltaX, deltaY) < 6) return;

    this.widgetDragMoved = true;
    this.suppressLauncherClick = true;
    const position = this.clampWidgetPosition(this.widgetDragStart.left + deltaX, this.widgetDragStart.top + deltaY);
    this.setWidgetPosition(position.left, position.top);
    event.preventDefault();
  }

  onWidgetPointerUp(event) {
    if (this.widgetPointerId !== event.pointerId) return;

    try {
      this.launcher?.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // Some browsers release pointer capture automatically before pointerup.
    }

    if (this.widgetDragMoved) {
      this.settleWidgetAtDroppedPosition();
      window.setTimeout(() => {
        this.suppressLauncherClick = false;
      }, 450);
      event.preventDefault();
    } else {
      this.dataset.dragging = 'false';
    }

    this.widgetPointerId = null;
    this.widgetDragStart = null;
    this.widgetDragMoved = false;
    this.markWidgetInteraction();
  }

  onWidgetPointerCancel(event) {
    if (this.widgetPointerId !== event.pointerId) return;
    try {
      this.launcher?.releasePointerCapture?.(event.pointerId);
    } catch (error) {
      // Some browsers release pointer capture automatically before pointercancel.
    }
    this.widgetPointerId = null;
    this.widgetDragStart = null;
    this.widgetDragMoved = false;
    this.dataset.dragging = 'false';
    this.applyStoredWidgetPosition();
    this.scheduleIdlePulse();
  }

  onTouchStart(event) {
    const touch = event.changedTouches[0];
    this.touchStart = { x: touch.clientX, y: touch.clientY };
  }

  onTouchEnd(event) {
    if (!this.touchStart) return;
    const touch = event.changedTouches[0];
    this.handleGesture(touch.clientX - this.touchStart.x, touch.clientY - this.touchStart.y);
    this.touchStart = null;
  }

  onMouseDown(event) {
    if (this.dataset.open !== 'true') return;
    if (event.button !== 0) return;
    if (this.isInteractiveGestureTarget(event.target)) return;

    this.mouseStart = { x: event.clientX, y: event.clientY };
    this.mouseTracked = true;
    this.mouseMoved = false;
    this.dataset.dragging = 'true';
    event.preventDefault();
  }

  onMouseMove(event) {
    if (!this.mouseTracked || !this.mouseStart) return;

    const deltaX = event.clientX - this.mouseStart.x;
    const deltaY = event.clientY - this.mouseStart.y;
    this.mouseMoved = this.mouseMoved || Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8;
    if (this.mouseMoved) event.preventDefault();
  }

  onMouseUp(event) {
    if (!this.mouseTracked || !this.mouseStart) return;

    this.handleGesture(event.clientX - this.mouseStart.x, event.clientY - this.mouseStart.y);
    this.resetMouse();
  }

  resetMouse() {
    this.mouseStart = null;
    this.mouseTracked = false;
    this.mouseMoved = false;
    this.dataset.dragging = 'false';
  }

  onDragStart(event) {
    if (this.dataset.open !== 'true') return;
    if (this.isInteractiveGestureTarget(event.target)) return;
    event.preventDefault();
  }

  onWheel(event) {
    if (this.dataset.open !== 'true') return;
    if (this.isInteractiveGestureTarget(event.target)) return;

    event.preventDefault();

    const now = performance.now();
    if (now - this.lastWheelNavigation < 450) return;

    this.wheelDeltaX += event.deltaX;
    this.wheelDeltaY += event.deltaY;
    window.clearTimeout(this.wheelResetTimer);
    this.wheelResetTimer = window.setTimeout(() => this.resetWheelGesture(), 160);

    const dominant = Math.abs(this.wheelDeltaX) > Math.abs(this.wheelDeltaY) ? this.wheelDeltaX : this.wheelDeltaY;
    if (Math.abs(dominant) < this.gestureThreshold) return;

    this.lastWheelNavigation = now;
    this.resetWheelGesture();
    this.go(dominant > 0 ? 1 : -1);
  }

  resetWheelGesture() {
    window.clearTimeout(this.wheelResetTimer);
    this.wheelResetTimer = null;
    this.wheelDeltaX = 0;
    this.wheelDeltaY = 0;
  }

  applyStoredWidgetPosition() {
    if (this.dataset.open === 'true') return;

    window.requestAnimationFrame(() => {
      const position = this.widgetPosition
        ? this.clampWidgetPosition(this.widgetPosition.left, this.widgetPosition.top)
        : this.getAnchorPosition(this.getDefaultAnchorName());
      this.setWidgetPosition(position.left, position.top);
    });
  }

  settleWidgetAtDroppedPosition() {
    const rect = this.getBoundingClientRect();
    const position = this.clampWidgetPosition(rect.left, rect.top);

    this.setWidgetPosition(position.left, position.top);
    this.storeWidgetPosition(position);
    window.setTimeout(() => {
      if (this.dataset.dragging === 'true') this.dataset.dragging = 'false';
    }, 0);
  }

  getWidgetAnchors() {
    const safe = this.getWidgetSafeBounds();
    const middleTop = safe.minTop + (safe.maxTop - safe.minTop) / 2;

    return [
      { name: 'top-left', left: safe.minLeft, top: safe.minTop },
      { name: 'top-right', left: safe.maxLeft, top: safe.minTop },
      { name: 'middle-left', left: safe.minLeft, top: middleTop },
      { name: 'middle-right', left: safe.maxLeft, top: middleTop },
      { name: 'bottom-left', left: safe.minLeft, top: safe.maxTop },
      { name: 'bottom-right', left: safe.maxLeft, top: safe.maxTop },
    ];
  }

  getAnchorPosition(name) {
    if (name === 'gallery-middle-right') {
      const galleryPosition = this.getGalleryAnchorPosition();
      if (galleryPosition) return galleryPosition;
    }

    return this.getWidgetAnchors().find((anchor) => anchor.name === name) || this.getWidgetAnchors()[4];
  }

  getDefaultAnchorName() {
    return window.matchMedia('(min-width: 750px)').matches ? 'gallery-middle-right' : 'bottom-left';
  }

  getGalleryAnchorPosition() {
    const gallery = document.querySelector(
      'main[data-template="product"] media-gallery slideshow-container, main[data-template="product"] .product-information__media media-gallery'
    );
    if (!(gallery instanceof HTMLElement)) return null;

    const rect = gallery.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;

    const width = this.offsetWidth || 86;
    const height = this.offsetHeight || 134;
    const productDetails = document.querySelector('main[data-template="product"] .product-details');
    const productDetailsRect = productDetails instanceof HTMLElement ? productDetails.getBoundingClientRect() : null;
    let left = rect.right + 12;

    if (productDetailsRect && productDetailsRect.left > rect.right && left + width > productDetailsRect.left - 12) {
      left = productDetailsRect.left - width - 12;
    }

    return this.clampWidgetPosition(left, rect.top + rect.height / 2 - height / 2);
  }

  clampWidgetPosition(left, top) {
    const safe = this.getWidgetSafeBounds();
    return {
      left: Math.min(Math.max(left, safe.minLeft), safe.maxLeft),
      top: Math.min(Math.max(top, safe.minTop), safe.maxTop),
    };
  }

  getWidgetSafeBounds() {
    const margin = 16;
    const width = this.offsetWidth || 74;
    const height = this.offsetHeight || 116;
    const bottomReserve = this.getBottomCollisionReserve(margin);

    return {
      minLeft: margin,
      maxLeft: Math.max(margin, window.innerWidth - width - margin),
      minTop: margin,
      maxTop: Math.max(margin, window.innerHeight - height - bottomReserve),
    };
  }

  getMaxScrollY() {
    if (this.scrollRoot instanceof HTMLElement) {
      return Math.max(0, this.scrollRoot.scrollHeight - this.scrollRoot.clientHeight);
    }

    const scrollingElement = document.scrollingElement || document.documentElement;
    return Math.max(0, scrollingElement.scrollHeight - window.innerHeight);
  }

  getScrollY() {
    if (this.scrollRoot instanceof HTMLElement) return this.scrollRoot.scrollTop;
    return window.scrollY;
  }

  getScrollRoot() {
    if (!window.matchMedia('(min-width: 750px)').matches) return window;

    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper instanceof HTMLElement) {
      const overflowY = window.getComputedStyle(pageWrapper).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return pageWrapper;
    }

    return window;
  }

  getClampedScrollY(scrollY = window.scrollY, maxScrollY = this.getMaxScrollY()) {
    if (!Number.isFinite(scrollY)) return 0;
    return Math.min(Math.max(scrollY, 0), maxScrollY);
  }

  getBottomCollisionReserve(baseMargin) {
    const selectors = [
      '.sticky-add-to-cart__bar[data-stuck="true"]',
      '.sticky-add-to-cart__bar',
      '#shopify-chat',
      '[id*="chat" i]',
      '[class*="chat" i]',
      '[id*="cookie" i]',
      '[class*="cookie" i]',
    ];
    let reserve = baseMargin;

    let collisionElements = [];

    try {
      collisionElements = Array.from(document.querySelectorAll(selectors.join(',')));
    } catch (error) {
      collisionElements = Array.from(document.querySelectorAll('.sticky-add-to-cart__bar, #shopify-chat'));
    }

    collisionElements.forEach((element) => {
      if (!(element instanceof HTMLElement) || this.contains(element)) return;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return;
      if (!['fixed', 'sticky'].includes(style.position)) return;

      const rect = element.getBoundingClientRect();
      const isBottomOverlay = rect.height > 0 && rect.width > 0 && rect.bottom > window.innerHeight - 220;
      if (!isBottomOverlay || rect.height > window.innerHeight * 0.55) return;

      reserve = Math.max(reserve, window.innerHeight - rect.top + baseMargin);
    });

    return reserve;
  }

  setWidgetPosition(left, top) {
    const position = this.clampWidgetPosition(left, top);
    this.widgetPosition = position;
    this.dataset.positioned = 'true';
    this.style.setProperty('--reel-left', `${position.left}px`);
    this.style.setProperty('--reel-top', `${position.top}px`);
  }

  getStoredWidgetPosition() {
    try {
      const stored = localStorage.getItem(this.positionStorageKey);
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (!Number.isFinite(parsed?.left) || !Number.isFinite(parsed?.top)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  storeWidgetPosition(position) {
    try {
      localStorage.setItem(this.positionStorageKey, JSON.stringify(position));
    } catch (error) {
      // Storage can be unavailable in private or restricted browsing contexts.
    }
  }

  getSessionMutedPreference() {
    try {
      const stored = sessionStorage.getItem(this.soundStorageKey);
      return stored === null ? false : stored === 'true';
    } catch (error) {
      return false;
    }
  }

  storeSessionMutedPreference() {
    try {
      sessionStorage.setItem(this.soundStorageKey, String(this.muted));
    } catch (error) {
      // Session storage is an enhancement only.
    }
  }

  updatePreviewPlayingState() {
    this.dataset.previewPlaying = String(Boolean(this.previewVideo && !this.previewVideo.paused && !this.previewVideo.ended));
  }

  markWidgetInteraction() {
    if (this.idlePulsePlayed) return;
    this.scheduleIdlePulse();
  }

  scheduleIdlePulse() {
    window.clearTimeout(this.idlePulseTimer);
    if (this.idlePulsePlayed || this.dataset.open === 'true' || this.dataset.dragging === 'true') return;

    this.idlePulseTimer = window.setTimeout(() => {
      if (this.dataset.open === 'true' || this.dataset.dragging === 'true' || this.dataset.dismissed === 'true') return;
      this.dataset.idlePulse = 'true';
      this.idlePulsePlayed = true;
      window.setTimeout(() => {
        this.dataset.idlePulse = 'false';
      }, 900);
    }, 10000);
  }

  stopIdlePulse() {
    window.clearTimeout(this.idlePulseTimer);
    this.dataset.idlePulse = 'false';
  }

  isInteractiveGestureTarget(target) {
    return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea, summary'));
  }

  handleGesture(deltaX, deltaY) {
    const dominant = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
    if (Math.abs(dominant) < this.gestureThreshold) return;
    this.go(dominant < 0 ? 1 : -1);
  }

  onKeydown(event) {
    if (this.dataset.open !== 'true') return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }

    if (['ArrowDown', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      this.go(1);
    }

    if (['ArrowUp', 'ArrowLeft'].includes(event.key)) {
      event.preventDefault();
      this.go(-1);
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    const focusable = Array.from(
      this.modal?.querySelectorAll('a[href], button:not([disabled]), video, [tabindex]:not([tabindex="-1"])') || []
    ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async onActionClick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-reel-add]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      if (!response.ok) throw new Error('Add to cart failed');
      const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json());
      button.textContent = 'Added';
      this.dispatchEvent(
        new CartAddEvent(cart, 'shoonyas-product-reel', {
          source: 'shoonyas-product-reel',
          itemCount: 1,
          variantId,
        })
      );
    } catch (error) {
      button.textContent = 'Try again';
      button.disabled = false;
    }

    window.setTimeout(() => {
      if (!button.isConnected) return;
      button.textContent = originalText;
      button.disabled = false;
    }, 1800);
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('shoonyas-product-reel')) {
  customElements.define('shoonyas-product-reel', ShoonyasProductReel);
}
